import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Mail, ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const LOGO = "https://customer-assets.emergentagent.com/job_korean-food-hub-1/artifacts/ghexjw3c_mokjaa.png";

export default function AuthPage() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [otp, setOtp] = useState("");
  const [stage, setStage] = useState("email"); // email | otp
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const { sendOtp, verifyOtp, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = new URLSearchParams(location.search).get("returnTo") || "/menu";

  useEffect(() => {
    if (user) navigate(returnTo, { replace: true });
  }, [user, navigate, returnTo]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleSend = async () => {
    if (!email || !/@/.test(email)) { toast.error("Enter a valid email"); return; }
    setLoading(true);
    try {
      const resp = await sendOtp(email);
      if (resp.dev_otp) toast.success(`OTP sent. Dev code: ${resp.dev_otp}`);
      else toast.success("OTP sent to your email");
      setStage("otp");
      setCooldown(60);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send OTP");
    } finally { setLoading(false); }
  };

  const handleVerify = async () => {
    if (otp.length !== 6) { toast.error("Enter 6 digit OTP"); return; }
    setLoading(true);
    try {
      await verifyOtp(email, otp, name);
      toast.success("Welcome!");
      navigate(returnTo, { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid OTP");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] grid place-items-center bg-white py-12 px-4" data-testid="auth-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <img src={LOGO} alt="Mok.Jaa" className="w-16 h-16 mx-auto rounded-full bg-black mb-5" />
          <h1 className="font-display font-black text-4xl tracking-tighter">{stage === "email" ? "Welcome" : "Check your email"}</h1>
          <p className="text-gray-500 mt-2 text-sm">
            {stage === "email" ? "Sign in or sign up using your email." : `We sent a 6-digit code to ${email}`}
          </p>
        </div>

        {stage === "email" && (
          <div className="space-y-5">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Your name (optional)</label>
              <input
                data-testid="auth-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border-b-2 border-gray-200 focus:border-black py-3 bg-transparent outline-none text-lg"
                placeholder="What should we call you?"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Email</label>
              <div className="flex items-center gap-2 border-b-2 border-gray-200 focus-within:border-black py-3">
                <Mail className="w-5 h-5 text-gray-400" />
                <input
                  data-testid="auth-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  className="flex-1 bg-transparent outline-none text-lg"
                  placeholder="you@example.com"
                />
              </div>
            </div>
            <button
              onClick={handleSend}
              disabled={loading}
              className="w-full bg-black text-white rounded-full py-4 font-bold inline-flex items-center justify-center gap-2 hover:bg-gray-800 disabled:bg-gray-400 transition"
              data-testid="send-otp-btn"
            >
              {loading ? "Sending..." : <>Send OTP <ArrowRight className="w-4 h-4" /></>}
            </button>
          </div>
        )}

        {stage === "otp" && (
          <div className="space-y-5">
            <div className="flex justify-center gap-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <input
                  key={i}
                  data-testid={`otp-${i}`}
                  value={otp[i] || ""}
                  maxLength={1}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "").slice(-1);
                    const arr = otp.split("");
                    arr[i] = v;
                    const next = arr.join("").slice(0, 6);
                    setOtp(next);
                    if (v && i < 5) {
                      const el = document.querySelector(`[data-testid="otp-${i + 1}"]`);
                      if (el) el.focus();
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Backspace" && !otp[i] && i > 0) {
                      const el = document.querySelector(`[data-testid="otp-${i - 1}"]`);
                      if (el) el.focus();
                    }
                  }}
                  className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-200 rounded-xl focus:border-black outline-none"
                  inputMode="numeric"
                />
              ))}
            </div>
            <button
              onClick={handleVerify}
              disabled={loading}
              className="w-full bg-black text-white rounded-full py-4 font-bold hover:bg-gray-800 disabled:bg-gray-400 transition"
              data-testid="verify-otp-btn"
            >
              {loading ? "Verifying..." : "Verify & Continue"}
            </button>
            <div className="text-center text-sm text-gray-500">
              {cooldown > 0 ? (
                <>Resend in {cooldown}s</>
              ) : (
                <button onClick={handleSend} className="font-bold text-black hover:underline" data-testid="resend-otp-btn">Resend OTP</button>
              )}
              <button onClick={() => setStage("email")} className="ml-3 text-gray-500 hover:text-black" data-testid="change-email-btn">
                Change email
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
